import React, { useEffect, useRef, useState } from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { theme } from '@fiverr-private/theme';
import { IconButton, Button } from '@fiverr-private/button';
import { CopyIcon } from '@fiverr-private/visuals';
import { AppI18n } from '../../../i18n';
import { useLandingPage } from '../../context/LandingPageContext';
import { INTERNAL_SERVER_ERROR_CODE } from '../../consts';
import { useRegenerateSellers } from '../../hooks/useRegenerateSellers';
import { useVerticalFlowEvents } from '../../hooks/useVerticalFlowEvents';
import { handleResponseFailure } from '../utils';
import { BriefBox } from './BriefBox';
import { BriefBoxSkeleton } from './BriefBox/skeleton';
import { HowItWorksPopover } from './HowItWorkPopover';

export const BriefEditor = () => {
    const { setBriefBoxInputValue, generatedBrief } = useLandingPage();
    const [errorElement, setErrorElement] = useState<React.ReactNode | null>(null);
    const [localValue, setLocalValue] = useState<string>('');
    const { regenerateSellers } = useRegenerateSellers();
    const { reportButtonClickEvent } = useVerticalFlowEvents('Update suggestions');
    const initialBriefValue = useRef<string>();

    useEffect(() => {
        if (generatedBrief) {
            setLocalValue(generatedBrief);
            setBriefBoxInputValue(generatedBrief);
            initialBriefValue.current = generatedBrief;
        }
    }, [generatedBrief, setBriefBoxInputValue]);

    const handleCopy = async () => {
        try {
            if (localValue) {
                await navigator.clipboard.writeText(localValue);
            }
        } catch (error) {
            setErrorElement(handleResponseFailure(INTERNAL_SERVER_ERROR_CODE));
        }
    };

    const handleUpdateSuggestions = async () => {
        try {
            if (!localValue) {
                return;
            }
            initialBriefValue.current = localValue;

            regenerateSellers();
        } catch (error) {
            setErrorElement(handleResponseFailure(INTERNAL_SERVER_ERROR_CODE));
        } finally {
            reportButtonClickEvent();
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setLocalValue(newValue);
        setBriefBoxInputValue(newValue);
    };

    const isBriefChanged = localValue?.trim() !== initialBriefValue.current?.trim();

    return (
        <Stack
            paddingX={{ default: '4', md: '8' }}
            paddingTop={{ default: '4', md: '8' }}
            paddingBottom="8"
            background={{ default: theme.greyColors.grey_100, md: theme.whiteColors.white }}
            width={{ default: '100%', md: '35%' }}
            direction="column"
            justifyContent="spaceBetween"
        >
            <Stack direction="column" width="100%" height="100%" gap={{ default: '8', md: '2' }}>
                {generatedBrief ? (
                    <BriefBox
                        value={localValue}
                        isBriefChanged={isBriefChanged}
                        onSuggestionsUpdate={handleUpdateSuggestions}
                        onChange={handleChange}
                        onCopy={handleCopy}
                    />
                ) : (
                    <BriefBoxSkeleton />
                )}
                <Container display="flex" justifyContent="flexEnd">
                    <HowItWorksPopover />
                </Container>
            </Stack>
            <Stack justifyContent="spaceBetween" hidden={{ default: true, md: !generatedBrief }}>
                <IconButton aria-label="copy" variant="ghost" onClick={handleCopy}>
                    <CopyIcon />
                </IconButton>
                <Button onClick={handleUpdateSuggestions} disabled={!isBriefChanged || localValue?.length === 0}>
                    <AppI18n k="brief_box.button_text" />
                </Button>
            </Stack>
            {errorElement}
        </Stack>
    );
};
